import React, { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export function OfferingsDialog({ open, onOpenChange, employer, products, onSaved }) {
  const [ids, setIds] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && employer) setIds(employer.offered_product_ids || []);
  }, [open, employer]);

  const toggle = (id) => setIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/employers/${employer.id}/offerings`, { offered_product_ids: ids });
      toast.success("Offerings updated");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-h-[85vh] overflow-y-auto" data-testid="offerings-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Manage offerings</DialogTitle>
          <DialogDescription>
            {employer?.name} — choose which products this employer offers. Selected products appear in that employer's employees' benefits catalog.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {products.length ? products.map((p) => (
            <label key={p.id} className="flex items-center gap-3 p-3 rounded-lg border border-mist-200 cursor-pointer hover:bg-mist-100" data-testid={`offering-${p.id}`}>
              <Checkbox checked={ids.includes(p.id)} onCheckedChange={() => toggle(p.id)} />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-navy-900 text-sm">{p.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{p.benefit_category_key} · {p.carrier?.name || "—"}</p>
              </div>
            </label>
          )) : <p className="text-sm text-muted-foreground text-center py-6">No products in the catalog yet.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-gold text-navy-900 hover:bg-gold-light font-bold" data-testid="save-offerings">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : `Save (${ids.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
